import { UnsupportedUploadError } from '../errors';
import type { FileUploadKind, ProxyUploadKind, UploaderState } from './index';

/**
 * The kind payload of a file upload. Reaching for it from a lifecycle that runs
 * for any other kind is a wiring mistake rather than a runtime condition, so it
 * throws rather than widening every caller to undefined.
 */
export function unwrapFileUploadKind<Meta, ChunkMeta>(
  state: UploaderState<Meta, ChunkMeta>,
): FileUploadKind {
  const uploadKind = state.uploadKind;
  if (uploadKind.kind !== 'file') {
    throw new UnsupportedUploadError(
      'File lifecycle used for a non-file upload',
    );
  }
  return uploadKind;
}

export function unwrapProxyUploadKind<Meta, ChunkMeta>(
  state: UploaderState<Meta, ChunkMeta>,
): ProxyUploadKind {
  const uploadKind = state.uploadKind;
  if (uploadKind.kind !== 'proxy') {
    throw new UnsupportedUploadError(
      'Proxy lifecycle used for a non-proxy upload',
    );
  }
  return uploadKind;
}

/**
 * State update that records entities on a file upload's kind. The kind is
 * replaced wholesale because state updates are a shallow merge.
 */
export function updateFileUploadKind<Meta, ChunkMeta>(
  state: UploaderState<Meta, ChunkMeta>,
  patch: Partial<Omit<FileUploadKind, 'kind'>>,
): Partial<UploaderState<Meta, ChunkMeta>> {
  return { uploadKind: { ...unwrapFileUploadKind(state), ...patch } };
}
